import React, { useState, useEffect } from 'react';

const EmailVerificationBanner = ({ user }) => {
  const [status, setStatus] = useState('idle'); // idle, sending, sent, error
  const [message, setMessage] = useState('');
  const [canResend, setCanResend] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  // Don't show banner if email is verified or user dismissed it
  if (user?.email_verified || dismissed) {
    return null;
  }

  const handleResendVerification = async () => {
    setStatus('sending');
    setMessage('');

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const token = localStorage.getItem('token');

      const response = await fetch(`${backendUrl}/api/auth/send-verification-email`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      // Handle response based on status code
      if (response.ok) {
        setStatus('sent');
        setMessage('Verification email sent! Check your inbox.');
        setCanResend(false);
        
        // Re-enable resend button after 5 minutes
        setTimeout(() => {
          setCanResend(true);
          setStatus('idle');
          setMessage('');
        }, 300000); // 5 minutes
      } else {
        // Try to parse error details, but handle clone errors gracefully
        let errorMessage = 'Failed to send verification email';
        console.log('[EmailVerification] Response status:', response.status);
        try {
          const data = await response.json();
          errorMessage = data.detail || errorMessage;
          console.log('[EmailVerification] Error from server:', data.detail);
        } catch (jsonError) {
          console.log('[EmailVerification] JSON parse failed, using status code');
          // If JSON parsing fails (e.g., body already consumed), use status code
          if (response.status === 429) {
            errorMessage = 'Please wait a few minutes before requesting another verification email';
          } else if (response.status === 400) {
            errorMessage = 'Email already verified or invalid request';
          }
        }
        
        console.log('[EmailVerification] Final error message:', errorMessage);
        setStatus('error');
        setMessage(errorMessage);
        
        // Clear error after 5 seconds
        setTimeout(() => {
          setStatus('idle');
          setMessage('');
        }, 5000);
      }
    } catch (error) {
      setStatus('error');
      setMessage('Network error. Please try again.');
      console.error('Resend verification error:', error);
      
      setTimeout(() => {
        setStatus('idle');
        setMessage('');
      }, 5000);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    // Store dismissal in session storage (not persistent across sessions)
    sessionStorage.setItem('emailVerificationBannerDismissed', 'true');
  };

  return (
    <div style={{
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      border: '1px solid #f59e0b',
      borderRadius: '8px',
      padding: '16px 20px',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      boxShadow: '0 2px 8px rgba(245, 158, 11, 0.1)'
    }}>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '12px' }}>
        {/* Warning Icon */}
        <div style={{
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          background: '#f59e0b',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '20px',
          color: 'white',
          flexShrink: 0
        }}>
          ⚠
        </div>

        {/* Content */}
        <div style={{ flex: 1 }}>
          <h3 style={{
            margin: '0 0 4px 0',
            fontSize: '16px',
            fontWeight: '600',
            color: '#92400e'
          }}>
            Email Not Verified
          </h3>
          <p style={{
            margin: 0,
            fontSize: '14px',
            color: '#78350f',
            lineHeight: '1.4'
          }}>
            {message || 'Verify your email to unlock premium upgrades, password changes, and full account features.'}
          </p>
        </div>

        {/* Actions */}
        <div style={{
          display: 'flex',
          gap: '10px',
          flexShrink: 0
        }}>
          <button
            onClick={handleResendVerification}
            disabled={!canResend || status === 'sending'}
            style={{
              background: canResend && status !== 'sending' ? '#f59e0b' : '#d1d5db',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              padding: '8px 16px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: canResend && status !== 'sending' ? 'pointer' : 'not-allowed',
              transition: 'all 0.2s',
              whiteSpace: 'nowrap'
            }}
            onMouseOver={(e) => {
              if (canResend && status !== 'sending') {
                e.target.style.background = '#d97706';
              }
            }}
            onMouseOut={(e) => {
              if (canResend && status !== 'sending') {
                e.target.style.background = '#f59e0b';
              }
            }}
          >
            {status === 'sending' ? 'Sending...' : 
             status === 'sent' ? 'Email Sent!' :
             !canResend ? 'Wait 5 min' : 
             'Resend Verification'}
          </button>

          <button
            onClick={handleDismiss}
            style={{
              background: 'transparent',
              color: '#92400e',
              border: '1px solid #f59e0b',
              borderRadius: '6px',
              padding: '8px 12px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.target.style.background = 'rgba(245, 158, 11, 0.1)';
            }}
            onMouseOut={(e) => {
              e.target.style.background = 'transparent';
            }}
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
